import type {
  GameAction,
  GamePhase,
  LaneId,
  PlayerObservationV1,
  VisibleArmyCardViewV1,
  VisibleResourcePoolV1,
} from "@fate-kingdom/engine";
import { useEffect, useRef } from "react";

import { getAlphaDisplayName } from "./presentation/alpha-card-presentation";

export interface AlphaPendingAction {
  readonly action: GameAction;
  readonly title: string;
  readonly description: string;
}

interface AlphaChoicePanelProps {
  readonly actions: readonly GameAction[];
  readonly disabled?: boolean;
  readonly highlighted?: boolean;
  readonly observation: PlayerObservationV1;
  readonly onChoose: (pending: AlphaPendingAction) => void;
}

const RESOURCE_LABELS: ReadonlyArray<readonly [keyof VisibleResourcePoolV1, string]> = [
  ["food", "Food"],
  ["production", "Production"],
  ["trade", "Trade"],
  ["science", "Science"],
  ["culture", "Culture"],
];

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function zeroResources(): VisibleResourcePoolV1 {
  return { food: 0, production: 0, trade: 0, science: 0, culture: 0 };
}

function addResources(
  left: VisibleResourcePoolV1,
  right: VisibleResourcePoolV1,
): VisibleResourcePoolV1 {
  return {
    food: left.food + right.food,
    production: left.production + right.production,
    trade: left.trade + right.trade,
    science: left.science + right.science,
    culture: left.culture + right.culture,
  };
}

function subtractResources(
  left: VisibleResourcePoolV1,
  right: VisibleResourcePoolV1,
): VisibleResourcePoolV1 {
  return {
    food: Math.max(0, left.food - right.food),
    production: Math.max(0, left.production - right.production),
    trade: Math.max(0, left.trade - right.trade),
    science: Math.max(0, left.science - right.science),
    culture: Math.max(0, left.culture - right.culture),
  };
}

function resourceShortfall(
  production: VisibleResourcePoolV1,
  upkeep: VisibleResourcePoolV1,
): VisibleResourcePoolV1 {
  return subtractResources(upkeep, production);
}

function formatResources(pool: VisibleResourcePoolV1): string {
  const entries = RESOURCE_LABELS.filter(([key]) => pool[key] > 0).map(
    ([key, label]) => `${label} ${String(pool[key])}`,
  );
  return entries.length === 0 ? "none" : entries.join(", ");
}

function armyForAction(
  observation: PlayerObservationV1,
  laneId: LaneId,
  armyCardInstanceId: string,
): VisibleArmyCardViewV1 | undefined {
  return observation.lanes[laneId].own.armies.find(
    (army) => army.cardInstanceId === armyCardInstanceId,
  );
}

function laneSupplyEvidence(
  observation: PlayerObservationV1,
  laneId: LaneId,
  removedArmy: VisibleArmyCardViewV1,
): { readonly current: string; readonly after: string } | null {
  const side = observation.lanes[laneId].own;
  const baseLand = side.land?.cards[0];
  const stage = side.land?.stage;
  const production = stage === undefined ? undefined : baseLand?.stages[stage - 1]?.production;
  if (production === undefined) return null;
  const currentUpkeep = side.armies.reduce(
    (total, army) => addResources(total, army.upkeep),
    zeroResources(),
  );
  const afterUpkeep = subtractResources(currentUpkeep, removedArmy.upkeep);
  return {
    current: formatResources(resourceShortfall(production, currentUpkeep)),
    after: formatResources(resourceShortfall(production, afterUpkeep)),
  };
}

function ChoiceButton({
  title,
  detail,
  consequence,
  ariaLabel,
  disabled = false,
  onClick,
}: {
  readonly title: string;
  readonly detail: string;
  readonly consequence: string;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      className="alpha-choice"
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
    >
      <b>{title}</b>
      <span>{detail}</span>
      <small>{consequence}</small>
    </button>
  );
}

export function AlphaSupplyChoicePanel({
  actions,
  disabled = false,
  observation,
  onChoose,
}: AlphaChoicePanelProps) {
  const supplyActions = actions.filter(
    (action): action is Extract<GameAction, { type: "discard-army-for-upkeep" }> =>
      action.type === "discard-army-for-upkeep",
  );
  if (supplyActions.length === 0) return null;

  return (
    <section className="alpha-actions" aria-labelledby="alpha-supply-title">
      <h2 id="alpha-supply-title">Required Supply choice</h2>
      <p className="alpha-actions__intro">
        Choose one Army to discard, review the effect, then confirm.
      </p>
      <div className="alpha-choice-list">
        {supplyActions.map((action) => {
          const army = armyForAction(observation, action.laneId, action.armyCardInstanceId);
          if (army === undefined) return null;
          const displayName = getAlphaDisplayName(army);
          const evidence = laneSupplyEvidence(observation, action.laneId, army);
          const laneName = titleCase(action.laneId);
          const detail = `Army · ${laneName} lane · Attack ${String(army.attack)} · Guard ${String(army.guard)} · Size ${String(army.armySize)} · Upkeep ${formatResources(army.upkeep)}`;
          const consequence =
            evidence === null
              ? "Discard this Army and recalculate local Supply."
              : `Current shortfall: ${evidence.current}. After removal: ${evidence.after}.`;
          return (
            <ChoiceButton
              key={`${action.laneId}-${action.armyCardInstanceId}`}
              title={displayName}
              detail={detail}
              consequence={consequence}
              ariaLabel={`Review Supply discard: ${displayName} in ${laneName} lane`}
              disabled={disabled}
              onClick={() =>
                onChoose({
                  action,
                  title: `Discard ${displayName} for Supply?`,
                  description: `${detail}. ${consequence}`,
                })
              }
            />
          );
        })}
      </div>
    </section>
  );
}

export function AlphaCommandChoicePanel({
  actions,
  disabled = false,
  observation,
  onChoose,
}: AlphaChoicePanelProps) {
  const commandActions = actions.filter(
    (action): action is Extract<GameAction, { type: "disband-army" | "disband-commander" }> =>
      action.type === "disband-army" || action.type === "disband-commander",
  );
  if (commandActions.length === 0) return null;

  return (
    <section className="alpha-actions" aria-labelledby="alpha-command-title">
      <h2 id="alpha-command-title">Command options</h2>
      <p className="alpha-actions__intro">
        Disbanding is voluntary and sends the chosen card to discard.
      </p>
      <div className="alpha-choice-list">
        {commandActions.map((action) => {
          const lane = observation.lanes[action.laneId].own;
          const card =
            action.type === "disband-army"
              ? lane.armies.find((army) => army.cardInstanceId === action.armyCardInstanceId)
              : lane.commander?.cardInstanceId === action.commanderCardInstanceId
                ? lane.commander
                : undefined;
          if (card === undefined) return null;
          const displayName = getAlphaDisplayName(card);
          const laneName = titleCase(action.laneId);
          const detail =
            card.type === "army"
              ? `Army · ${laneName} lane · Attack ${String(card.attack)} · Guard ${String(card.guard)} · Size ${String(card.armySize)} · Upkeep ${formatResources(card.upkeep)}`
              : `Commander · ${laneName} lane · Attack ${String(card.attack ?? 0)} · Guard ${String(card.guard ?? 0)} · Capacity ${String(card.armyCapacity ?? 0)}`;
          const consequence =
            card.type === "army"
              ? "Send this Army to discard; lane Attack, Guard, Size and future Upkeep decrease immediately."
              : "Send this Commander to discard; stationed Armies remain but cannot normally attack without a Commander.";
          return (
            <ChoiceButton
              key={`${action.type}-${action.laneId}-${card.cardInstanceId}`}
              title={`Disband ${displayName}`}
              detail={detail}
              consequence={consequence}
              ariaLabel={`Review disband: ${displayName}, ${card.type}, ${laneName} lane`}
              disabled={disabled}
              onClick={() =>
                onChoose({
                  action,
                  title: `Disband ${displayName}?`,
                  description: `${detail}. ${consequence}`,
                })
              }
            />
          );
        })}
      </div>
    </section>
  );
}

function visibleAttack(observation: PlayerObservationV1, laneId: LaneId): number {
  const side = observation.lanes[laneId].own;
  if (side.commander === null) return 0;
  return (
    side.commander.attack! +
    side.armies.reduce((total, army) => total + army.attack, 0) +
    (side.land?.attackBonus ?? 0)
  );
}

function visibleGuard(observation: PlayerObservationV1, laneId: LaneId): number {
  const side = observation.lanes[laneId].opponent;
  return (
    (side.commander?.guard ?? 0) +
    side.armies.reduce((total, army) => total + army.guard, 0) +
    (side.land?.guardBonus ?? 0)
  );
}

export function AlphaBattleChoicePanel({
  actions,
  disabled = false,
  highlighted = false,
  observation,
  onChoose,
}: AlphaChoicePanelProps) {
  const attackActions = actions.filter(
    (action): action is Extract<GameAction, { type: "attack" }> => action.type === "attack",
  );
  if (attackActions.length === 0) return null;

  return (
    <section
      className={`alpha-actions ${highlighted ? "is-tutorial-highlight" : ""}`}
      aria-labelledby="alpha-battle-title"
    >
      <h2 id="alpha-battle-title">Battle options</h2>
      <p className="alpha-actions__intro">
        Damage is a visible estimate. Every listed attack remains legal, including a fully guarded
        attack.
      </p>
      <div className="alpha-choice-list">
        {attackActions.map((action) => {
          const attack = visibleAttack(observation, action.laneId);
          const guard = visibleGuard(observation, action.laneId);
          const damage = Math.max(0, attack - guard);
          const laneName = titleCase(action.laneId);
          const detail = `${laneName} lane · Visible Attack ${String(attack)} · opposing Guard ${String(guard)}`;
          const consequence = `Possible Control damage: ${String(damage)}.`;
          return (
            <ChoiceButton
              key={action.laneId}
              title={`Attack ${laneName}`}
              detail={detail}
              consequence={consequence}
              ariaLabel={`Review attack from ${laneName} lane: ${String(attack)} Attack against ${String(guard)} Guard, possible damage ${String(damage)}`}
              disabled={disabled}
              onClick={() =>
                onChoose({
                  action,
                  title: `Attack from ${laneName} lane?`,
                  description: `${detail}. ${consequence}`,
                })
              }
            />
          );
        })}
      </div>
    </section>
  );
}

const NEXT_PHASE: Readonly<Record<GamePhase, string>> = {
  ready: "Draw",
  draw: "Land",
  land: "Supply",
  supply: "Command",
  command: "Battle",
  battle: "Cleanup",
  cleanup: "the next turn",
  "action-window": "the next turn",
  finished: "match complete",
};

export function AlphaPhaseControl({
  actions,
  disabled = false,
  highlighted = false,
  phase,
  territoryDeadline = false,
  onChoose,
}: {
  readonly actions: readonly GameAction[];
  readonly disabled?: boolean;
  readonly highlighted?: boolean;
  readonly phase: GamePhase;
  /** The engine will end the match if this Land phase is advanced without a Land. */
  readonly territoryDeadline?: boolean;
  readonly onChoose: (pending: AlphaPendingAction) => void;
}) {
  const advance = actions.find(
    (
      action,
    ): action is
      Extract<GameAction, { type: "advance-phase" }> | Extract<GameAction, { type: "end-turn" }> =>
      action.type === "advance-phase" || action.type === "end-turn",
  );
  if (advance === undefined) return null;
  const phaseName = phase === "action-window" ? "Turn" : titleCase(phase);
  const next = NEXT_PHASE[phase];
  return (
    <section
      className={`alpha-phase-control ${highlighted ? "is-tutorial-highlight" : ""}`}
      aria-label="Phase control"
    >
      <button
        className="alpha-button alpha-button--secondary"
        type="button"
        disabled={disabled}
        aria-label={
          advance.type === "end-turn"
            ? "End Turn"
            : `End ${phaseName} Phase and continue to ${next}`
        }
        onClick={() =>
          onChoose({
            action: advance,
            title: territoryDeadline
              ? "End Land Phase and lose the match?"
              : advance.type === "end-turn"
                ? "End Turn?"
                : `End ${phaseName} Phase?`,
            description: territoryDeadline
              ? "You have no active Land while your opponent's Kingdom is Complete. Found a Land now, or confirm to end the match."
              : advance.type === "end-turn"
                ? "Discard your remaining hand, clear temporary turn resources, and hand play to the opponent."
                : `Finish your current ${phaseName} Phase and continue to ${next}.`,
          })
        }
      >
        {territoryDeadline
          ? "End Land Phase and Lose"
          : advance.type === "end-turn"
            ? "End Turn"
            : `End ${phaseName} Phase`}
      </button>
    </section>
  );
}

export function AlphaActionConfirmation({
  pending,
  highlighted = false,
  onConfirm,
  onCancel,
}: {
  readonly pending: AlphaPendingAction;
  readonly highlighted?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <aside
      className={`alpha-confirm ${highlighted ? "is-tutorial-highlight" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alpha-confirm-title"
      aria-describedby="alpha-confirm-description"
    >
      <div>
        <p id="alpha-confirm-title">{pending.title}</p>
        <small id="alpha-confirm-description">{pending.description}</small>
      </div>
      <button
        ref={confirmRef}
        className="alpha-button alpha-button--primary"
        type="button"
        onClick={onConfirm}
      >
        Confirm
      </button>
      <button className="alpha-button alpha-button--secondary" type="button" onClick={onCancel}>
        Cancel
      </button>
    </aside>
  );
}
